import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, Mountain, Bike, Gauge, Plane, Landmark, Flower2, MapPin, Youtube, Star, Quote, Headphones, Mic } from "lucide-react";
import { getTours } from "../lib/adventureApi";
import { useSeo } from "../lib/seo";
import RALayout from "../components/ra/RALayout";
import TourCard from "../components/ra/TourCard";
import MapPlanner from "../components/ra/MapPlanner";
import PodcastPlayer from "../components/ra/PodcastPlayer";
import RecentAdventure from "../components/ra/RecentAdventure";
import PartnersStrip from "../components/ra/PartnersStrip";
import VideoReviewsStrip from "../components/ra/VideoReviewsStrip";

const CATS = [
  { id: "trekking", label: "Trekking", icon: Mountain, blurb: "Wake above the clouds and earn views that redraw what you thought possible" },
  { id: "wellness", label: "Wellness", icon: Flower2, blurb: "Slow down, breathe deep and come home calmer, stronger and clearer" },
  { id: "motorcycle", label: "Motorcycle", icon: Gauge, blurb: "Chase mountain horizons on a Royal Enfield, one legendary pass at a time" },
  { id: "cycling", label: "Cycling", icon: Bike, blurb: "Pedal the high Himalaya, then let gravity take over on raw mountain singletrack" },
  { id: "flying", label: "Flying", icon: Plane, blurb: "Soar past Everest and land where few ever stand — in a single morning" },
  { id: "culture", label: "Cultural", icon: Landmark, blurb: "Live the festivals, feasts and traditions that make the Himalaya unforgettable" },
];

const U = (id) => `https://images.unsplash.com/${id}?q=80&w=1200&auto=format&fit=crop`;
const DESTS = [
  { slug: "nepal", name: "Nepal", tag: "The roof of the world", img: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?q=80&w=1200&auto=format&fit=crop" },
  { slug: "bhutan", name: "Bhutan", tag: "The last Himalayan kingdom", img: U("photo-1762698336057-05c3260fcd17") },
  { slug: "tibet", name: "Tibet", tag: "The sacred plateau", img: "/api/media/wp/2025/03/Devotees-hiking-to-Mt.-Kailash.png" },
  { slug: "india", name: "India — Himalaya", tag: "Spiti, Ladakh & high passes", img: U("photo-1762696782497-2b39df0f4523") },
  { slug: "kyrgyzstan", name: "Kyrgyzstan", tag: "The celestial mountains", img: U("photo-1689788648053-cd482d1acd9c") },
  { slug: "pakistan", name: "Pakistan", tag: "The Karakoram", img: U("photo-1562913346-61ae3ab9277e") },
];

const FILMS = [
  { id: "cwzvcuNvbR0", title: "Reviews from happy travellers around the world" },
  { id: "EK_cl157-CY", title: "Outside-the-box trekking in Nepal & the Himalaya" },
  { id: "KLyKb7EXVaw", title: "What makes Responsible Adventures different" },
  { id: "QWhUKuXknEw", title: "Off the beaten path in Nepal" },
  { id: "h9jVb-btQL8", title: "Meet founder Raj Tamang — luxury adventure Q&A" },
  { id: "8-fQs1OgNTQ", title: "Ghorepani trek" },
];

const TESTIMONIALS = [
  { name: "Barnaby D", title: "Extremely ethical company", text: "His ethical approach to inclusivity and carbon offsetting is so impressive — the company doesn't stop at carbon neutral, it's carbon negative. Guides, porters, ground staff, cooks: all were world class. Responsible Adventures took care of everything. Thank you Raj!" },
  { name: "charlie c", title: "Excellent trip in Nepal", text: "Thank you to Raj and his team for looking after us so well throughout our time in Nepal. Our guide Sundar was excellent — filtered water always available, fresh coffee for breakfast, fresh fruit after dinner: the smaller things that made a big difference." },
  { name: "Kalilovestotravel", title: "An incredible trip", text: "Ohh wow. My partner and I have had the trip of our lives — incredible experience, great team behind us. Raj tailor-made our trip based on our experience and capabilities. Massive thanks to the team, you guys are incredible." },
  { name: "Wade G", title: "Off the beaten track", text: "A fabulous trek — we completed the Khopra Ridge, well away from busy routes like ABC. We were often the only visitors in the tea house. Raj was easy to deal with and answered all our questions. I thoroughly recommend them." },
];

export default function RAHome() {
  useSeo({ title: "Responsible Adventures | Small-group adventures in Nepal & beyond", description: "Handcrafted, carbon-neutral small-group adventures in Nepal, Bhutan, Tibet, India, Kyrgyzstan and Pakistan: wellness treks, cycling, motorcycle tours, helicopter flights and cultural journeys.", path: "/" });
  const [featured, setFeatured] = useState([]);
  const [podcastPicks, setPodcastPicks] = useState([]);

  useEffect(() => {
    getTours({ destination: "nepal" }).then((all) => {
      const detailed = all.filter((t) => t.has_detail);
      setFeatured((detailed.length ? detailed : all).slice(0, 6));
      // feature prewarmed flagship episodes so they play instantly
      const picks = ["everest-base-camp-wellness-adventure",
        "annapurna-base-camp-via-poon-hill-wellness-trek",
        "manaslu-circuit-trekking-wellness-tour"];
      const bySlug = Object.fromEntries(all.map((t) => [t.slug, t]));
      const chosen = picks.map((s) => bySlug[s]).filter(Boolean);
      setPodcastPicks(chosen.length ? chosen : (detailed.length ? detailed : all).slice(0, 3));
    }).catch(() => {});
  }, []);

  return (
    <RALayout>
      {/* Hero */}
      <section className="relative min-h-[78vh] flex items-end overflow-hidden" data-testid="ra-hero">
        <img src="https://images.unsplash.com/photo-1544735716-392fe2489ffa?q=80&w=2000&auto=format&fit=crop"
          alt="Himalaya" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/10" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-16">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="font-mono text-xs tracking-[0.3em] uppercase text-white/70 mb-4">
            Nepal · Bhutan · India · Tibet
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-display font-extrabold text-4xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[0.95] max-w-4xl">
            Beyond the ordinary,<br />into the Himalaya.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mt-6 text-lg text-white/85 max-w-2xl leading-relaxed">
            Stand beneath 8,000-metre giants, share butter tea in Sherpa villages and breathe again in the
            world's wildest places — journeys crafted to leave you lighter than you arrived.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
            className="mt-8 flex flex-wrap gap-3">
            <Link to="/tours" data-testid="hero-explore-btn"
              className="flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-6 h-12 rounded-sm font-semibold tracking-wide transition-colors">
              Browse ready journeys <ArrowRight className="w-4 h-4" />
            </Link>
            <MapPlanner
              triggerClassName="flex items-center gap-2 bg-moss hover:bg-moss-hover text-white px-6 h-12 rounded-sm font-semibold tracking-wide transition-colors"
              triggerLabel="Plan on the map" />
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.28 }}
            className="mt-4 text-sm text-white/60">
            Two ways in: browse our crafted journeys, or explore the map and build your own.
            {" "}
            <Link to="/build" data-testid="hero-portal-btn" className="text-white/85 underline underline-offset-2 hover:text-white">Trade partner? Enter the portal →</Link>
          </motion.p>
        </div>
      </section>

      {/* Recent Adventure video (editable) */}
      <RecentAdventure />

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">Ways to travel</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {CATS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <Link to={`/tours?category=${c.id}`} data-testid={`home-cat-${c.id}`}
                  className="group flex flex-col h-full p-5 rounded-md border border-sand-dark bg-white hover:border-terracotta hover:-translate-y-1 transition-all">
                  <div className="w-11 h-11 rounded-sm bg-moss-light flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-moss" />
                  </div>
                  <h3 className="font-display font-bold tracking-tight">{c.label}</h3>
                  <p className="text-xs text-ink-muted mt-1 leading-relaxed">{c.blurb}</p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Destinations */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-4 pb-12">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">Where we go</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-destinations">
          {DESTS.map((d, i) => (
            <motion.div key={d.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}>
              <Link to={`/destinations/${d.slug}`} data-testid={`home-dest-${d.slug}`}
                className="group relative block h-56 rounded-md overflow-hidden">
                <img src={d.img} alt={d.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-5">
                  <h3 className="font-display font-extrabold text-white text-xl tracking-tight">{d.name}</h3>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/70 mt-0.5">{d.tag}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured tours */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-4">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Featured journeys</h2>
          <Link to="/tours" className="font-mono text-xs uppercase tracking-widest text-terracotta hover:underline">View all →</Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-featured">
          {featured.map((t, i) => <TourCard key={t.slug} tour={t} index={i} />)}
        </div>
      </section>

      {/* Video band */}
      <section className="bg-ink py-20" data-testid="home-video-band">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-2 mb-2 text-white/70">
            <Youtube className="w-5 h-5 text-terracotta" />
            <span className="font-mono text-xs uppercase tracking-widest">Films from the trail</span>
          </div>
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-8">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-white">See the Himalaya in motion</h2>
            <a href="https://www.youtube.com/@notjusttreks" target="_blank" rel="noreferrer"
              className="font-mono text-xs uppercase tracking-widest text-terracotta hover:underline" data-testid="home-youtube-channel">
              @notjusttreks →
            </a>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            {FILMS.map((f, i) => (
              <motion.figure key={f.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} data-testid={`home-film-${f.id}`}>
                <div className="aspect-video rounded-md overflow-hidden bg-black">
                  <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${f.id}`}
                    title={f.title} loading="lazy" frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen />
                </div>
                <figcaption className="text-sm text-white/60 mt-2.5">{f.title}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Podcast teaser — Hear what to expect */}
      {podcastPicks.length > 0 && (
        <section className="bg-sand py-20" data-testid="home-podcast-teaser">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="flex items-center gap-2 mb-2 text-moss">
              <Mic className="w-5 h-5 text-terracotta" />
              <span className="font-mono text-xs uppercase tracking-widest">Podcast · Raj &amp; Simone</span>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-8">
              <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">Hear what to expect</h2>
              <p className="text-sm text-ink-secondary max-w-md">Founder Raj and traveller Simone talk you through the trip — the real feel, drawn from our own journal. Press play.</p>
            </div>
            <div className="grid gap-5 sm:grid-cols-3">
              {podcastPicks.slice(0, 3).map((tour, i) => (
                <motion.div key={tour.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }} data-testid={`home-podcast-${tour.slug}`}
                  className="group relative overflow-hidden rounded-md border border-sand-dark bg-white">
                  <div className="relative h-48 overflow-hidden">
                    <img src={tour.hero_image} alt={tour.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
                    <span className="absolute top-3 left-3 flex items-center gap-1.5 bg-ink/70 backdrop-blur text-white font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full">
                      <Headphones className="w-3 h-3 text-terracotta" /> Episode
                    </span>
                    <h3 className="absolute bottom-3 left-4 right-4 font-display font-bold text-lg text-white tracking-tight leading-snug">{tour.name}</h3>
                  </div>
                  <div className="p-4">
                    <PodcastPlayer slug={tour.slug} tourName={tour.name} triggerLabel="Play episode"
                      triggerClassName="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 rounded-sm font-semibold text-sm tracking-wide transition-colors" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <VideoReviewsStrip />

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="home-testimonials">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-8">
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-terracotta text-terracotta" />)}
            </div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Loved by travellers on Tripadvisor</h2>
          </div>
          <a href="https://www.tripadvisor.com.au/Attraction_Review-g293890-d10202865-Reviews-Responsible_Adventures-Kathmandu_Kathmandu_Valley_Bagmati_Zone_Central_Region.html"
            target="_blank" rel="noreferrer" data-testid="home-tripadvisor-link"
            className="font-mono text-xs uppercase tracking-widest text-terracotta hover:underline">Read all reviews →</a>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {TESTIMONIALS.map((t, i) => (
            <motion.blockquote key={t.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }}
              className="flex flex-col p-6 rounded-md border border-sand-dark bg-white" data-testid={`testimonial-${i}`}>
              <Quote className="w-6 h-6 text-terracotta/40 mb-3" />
              <p className="font-display font-bold text-sm tracking-tight mb-2">{t.title}</p>
              <p className="text-sm text-ink-secondary leading-relaxed flex-1">{t.text}</p>
              <footer className="mt-4 pt-4 border-t border-sand-dark flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-widest text-ink-muted">{t.name}</span>
                <div className="flex gap-0.5">{[...Array(5)].map((_, s) => <Star key={s} className="w-3 h-3 fill-terracotta text-terracotta" />)}</div>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </section>

      {/* Why RA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="rounded-lg bg-moss text-white p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-2 mb-4 text-white/80"><Leaf className="w-5 h-5" /><span className="font-mono text-xs uppercase tracking-widest">Carbon-neutral travel</span></div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight leading-tight">Every detail handled, so you can simply be present.</h2>
          </div>
          <p className="text-white/85 leading-relaxed">
            From your first hello to the summit selfie, we take care of everything — hand-picked lodges,
            superfood meals that keep you strong at altitude, and a warm, expert crew who treat you like family.
            You focus on the sunrise, the laughter and the sense of achievement; we handle the rest. And because
            every journey is carbon-neutral, the memories you make cost the mountains nothing.
          </p>
        </div>
      </section>

      {/* Partnerships & Nominations */}
      <PartnersStrip />
    </RALayout>
  );
}
